import { Icon } from '@bosch/react-frok'
import { sanitizeText, sanitizeUrl } from '@/utils/sanitization'
import { formatTimestamp, getTimestampRefreshDelay } from '@/utils/chatUtils'
import { useTranslation } from 'react-i18next'
import MessageWrapper from '@/ui/components/MessageWrapper/MessageWrapper'
import AttachmentGallery from '@/ui/components/AttachmentGallery/AttachmentGallery'
import type { AttachmentGalleryItem } from '@/ui/components/AttachmentGallery/types'
import { UserMessageProps } from './types/UserMessageTypes'

import './UserMessage.scss'
import { useEffect, useState } from 'react'

const normalizeUrls = (urls: string[] | string | null | undefined): string[] => {
    if (!urls) return []
    if (Array.isArray(urls)) return urls.filter(Boolean)
    try {
        const parsed = JSON.parse(urls)
        if (Array.isArray(parsed)) return parsed.filter(Boolean)
    } catch {
        return urls.split(',').map(s => s.trim()).filter(Boolean)
    }
    return []
}

const UserMessage = ({ message, userImage, activeAssistantConfig }: UserMessageProps) => {
    const initialTimestamp = message?.timestamp || Date.now();
    const { t } = useTranslation('general')

    const [timeStamp, setTimestamp] = useState<string>(() => formatTimestamp(initialTimestamp, t)); 
    const refreshDelay = getTimestampRefreshDelay(initialTimestamp)
    const rawMessage = message?.content || message?.message
    const parsedMessage = sanitizeText(rawMessage ?? '')
    const outputFeatures = activeAssistantConfig?.features?.output

    const fileUrls = normalizeUrls(message?.file_urls)
    const previewUrls = normalizeUrls(message?.preview_urls)
    const base64 = message?.attachmentBase64

    const galleryItems: AttachmentGalleryItem[] = base64
        ? [{ thumb: base64, full: base64 }]
        : previewUrls.length > 0
            ? previewUrls.map((p, i) => ({ thumb: p, full: sanitizeUrl(fileUrls[i] ?? p) ?? p }))
            : fileUrls.map(u => ({ thumb: u, full: u }))

    useEffect(() => {
        if (!initialTimestamp || refreshDelay <= 0) return

        const intervalId = setInterval(() => {
            setTimestamp(formatTimestamp(initialTimestamp, t))
        }, refreshDelay)

        return () => clearInterval(intervalId)
    }, [initialTimestamp, refreshDelay, t])

    return (
        <div className='user-message'>
            <div className='message-wrapper'>
                <div className='sender-icon'>
                    {userImage ? (
                        <div className='sender-icon-image-container'>
                            <img className='sender-icon-image' src={userImage} alt='user' />
                        </div>
                    ) : (
                        <Icon iconName='user' />
                    )}
                </div>
                <MessageWrapper
                    content={rawMessage || ''}
                    hasContent={!!rawMessage}
                    align='right'
                    fullWidth={false}
                    outputFeatures={outputFeatures}
                    messageType='user'
                >
                    <div className='message-box'>
                        <div className='message-content'>
                            <div className='message-text'>
                                <p>{parsedMessage}</p>
                            </div>
                            {galleryItems.length > 0 && (
                                <AttachmentGallery items={galleryItems} />
                            )}
                            <div className='message-timestamp'>
                                {timeStamp}
                            </div>
                        </div>
                    </div>
                </MessageWrapper>
            </div>
        </div>
    )
}

export default UserMessage
